import type {
  Product,
  ProductDescription,
  S3LabResult,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import { Accordion } from '@components/Accordion';
import { BlogCard } from '@components/Blog/BlogCard';
import { Button } from '@components/Button';
import { EmblaCarousel } from '@components/Carousels/Carousel';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { Icons } from '@components/Icons/Icons';
import { LoginModalUncontrolled } from '@components/LoginForm/LoginModal';
import { LinkRenderer } from '@components/PortableText/Link';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { LabResultList } from '@components/Product/LabResultList';
import { ReviewList } from '@components/Reviews/ReviewList';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useCustomer } from '@hooks/customer/useCustomer';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { forwardRef, type ReactNode, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface ProductAccordionProps {
  description: ProductDescription[];
  labResults: S3LabResult[];
  product: Product;
  defaultTabsOpen?: boolean;
}

const AccordionLinkRenderer = (props, id, ref) =>
  props.value?.href[0] === '#' ? (
    <button
      type="button"
      className={twMerge([
        'border-none bg-inherit p-0 text-start text-blue',
        'clickable',
      ])}
      onClick={() => {
        const targetId = props.value.href.slice(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          const detailsToOpen: HTMLDetailsElement[] = [];
          let current = targetElement.closest(
            'details'
          ) as HTMLDetailsElement | null;

          while (current) {
            detailsToOpen.unshift(current);
            current = current.parentElement?.closest(
              'details'
            ) as HTMLDetailsElement | null;
          }

          detailsToOpen.forEach((details) => {
            details.open = true;
          });

          requestAnimationFrame(() => {
            targetElement.scrollIntoView({ behavior: 'smooth' });
          });
        }
      }}
    >
      {props.children}
    </button>
  ) : (
    <LinkRenderer {...props} />
  );

export const ProductAccordion = forwardRef<
  HTMLDivElement,
  ProductAccordionProps
>(({ description, product, labResults, defaultTabsOpen = true }, ref) => {
  const { customer } = useCustomer();

  // handle tabs so that the tabs get closed when product changes
  const [openTabs, setOpenTabs] = useState<number[]>([]);

  const RendererOverride = {
    block: {
      h2: ({ children }: { children?: ReactNode }) => (
        <VisuallyHidden>
          <h2 className="font-bold">{children}</h2>
        </VisuallyHidden>
      ),
      h3: (props: any) => (
        <>
          {!props.value.isFirstH3 && (
            <HorizontalDivider className="via-grey-mid" />
          )}
          <h3 className="font-bold">{props.children}</h3>
        </>
      ),
    },
    marks: {
      link: (props) => AccordionLinkRenderer(props, 'References', ref),
    },
  };

  useEffect(() => {
    const isDesktop = window.innerWidth >= 768;
    setOpenTabs(isDesktop && defaultTabsOpen ? [0, 1] : []);
  }, [product.entityId, defaultTabsOpen]);

  const getPortableTextBlocks = (block: ProductDescription) => {
    const isFirstH3 = (index: number) =>
      !block.contentBody?.slice(0, index).some((item) => item.style === 'h3');

    const content =
      block.contentBody?.map((b, idx) =>
        b.style === 'h3' ? { ...b, isFirstH3: isFirstH3(idx) } : b
      ) || [];

    return (
      <PortableTextBlockRenderer
        content={content}
        componentsOverride={RendererOverride}
      />
    );
  };

  const getArticleBlock = (block: ProductDescription) => {
    if (block.relatedBlogs && block.relatedBlogs.length > 0) {
      return (
        <div className="py-2 my-6">
          <EmblaCarousel
            title={
              block.contentDescription.toLowerCase() === 'recipes'
                ? `${product.name} Recipes`
                : `Further Reading on ${product.name}`
            }
            slides={block.relatedBlogs.map((blog, idx) => (
              <BlogCard
                blog={blog}
                key={`featuredBlog${idx}`}
                className="min-w-[200px]"
              />
            ))}
          />
        </div>
      );
    }

    // if block is lab test results
    if (block.contentDescription === 'Lab Results') {
      return (
        <div className="my-6">
          {getPortableTextBlocks(block)}
          {labResults.length ? <LabResultList items={labResults} /> : null}
        </div>
      );
    }

    if (block.contentDescription === 'Nutrition Panels') {
      return (
        <div className="my-6">
          <div className="max-w-screen grid grid-cols-1 justify-center gap-4 md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2 [&>*:not(table)]:col-span-full">
            {getPortableTextBlocks(block)}
          </div>
        </div>
      );
    }
    return <div className="my-6">{getPortableTextBlocks(block)}</div>;
  };

  const items = [] as Array<{
    title: string | React.ReactNode;
    content: React.ReactNode;
    id: string;
  }>;

  for (const block of description) {
    items.push({
      title: block.contentDescription,
      content: getArticleBlock(block),
      id: block.contentDescription.split(' ').join('-'),
    });
  }

  // include reviews section at the bottom
  items.push({
    id: 'Reviews',
    title: 'Reviews',
    content: (
      <div id="Reviews" className="my-6">
        {customer ? (
          <div className="mb-4">
            Looking to review {product.name}? See which products you're{' '}
            <TextLink href="/account/reviews">eligible to review</TextLink>.
          </div>
        ) : (
          <div className="mb-4">
            Reviews can only be left by customers who have made a purchase with
            a registered Bulk Nutrients account.
            <br />
            <br />
            <LoginModalUncontrolled
              trigger={
                <Button buttonStyle="text" className="inline">
                  Sign in
                </Button>
              }
              title={<>Login to your account</>}
            />{' '}
            to see which products you can review.
          </div>
        )}
        {product.reviewSummary && (
          <div className="mb-4 bg-grey-light dark:bg-grey-darkest p-4 rounded-2xl">
            <h3 className="text-lg font-bold text-orange italic flex items-center gap-2 mb-2">
              <Icons.Sparkles.Outline className="text-black dark:text-white" />
              What Our Customers Are Saying
            </h3>
            <p>{product.reviewSummary}</p>
          </div>
        )}
        <ReviewList productId={product.entityId} />
      </div>
    ),
  });

  return (
    <Accordion
      key={`${product.entityId}-accordion`}
      items={items.map((item, index) => ({
        ...item,
        props: {
          'data-umami-event': 'hue-product-accordion-interaction',
          'data-umami-event-item': item.title,
          'data-umami-event-index': index,
          'data-umami-event-id': item.id,
        },
      }))}
      variant="pill"
      defaultOpen={false}
      openIndexes={openTabs}
    />
  );
});

ProductAccordion.displayName = 'ProductAccordion';
